import type { Validation } from "@/lib/validation"
import { isArray } from "@/lib/validation/isArray"
import { isExpectationArray } from "@/lib/validation/isExpectationArray"
import { isStringKeyedRecord } from "@/lib/validation/isStringKeyedRecord"
import { isReferenceContainer } from "@/lib/validation/reference/isContainer"
import { referenceToken } from "@/lib/validation/referenceToken"

export const isExpectation = <
  Registry extends Validation.BaseRegistry,
  Subject
>(
  api: Validation.API<Registry>,
  expectation: unknown,
  warn?: boolean
): expectation is Validation.Expectation<Registry, Subject> => {
  if (isReferenceContainer(api, expectation)) {
    return true
  }

  if (isArray(expectation)) {
    return isExpectationArray(api, expectation, warn)
  }

  if (isStringKeyedRecord(expectation)) {
    if (expectation[referenceToken]) {
      return false
    }

    const failures = []

    for (const key in expectation) {
      if (!isExpectation(api, expectation[key], warn)) {
        failures.push({ key, value: expectation[key] })
      }
    }

    if (failures.length === 0) {
      return true
    }

    if (warn && api.debugEnabled()) {
      api.privateFunctions.debug(
        `Validation#isExpectation found invalid record schema:`,
        {
          subject: expectation,
          failures,
        }
      )
    }

    return false
  }

  if (
    typeof expectation === `string` ||
    expectation === null ||
    expectation === undefined
  ) {
    return true
  }

  if (warn && api.debugEnabled()) {
    api.privateFunctions.debug(
      `Validation#isExpectation found invalid primitive expectation:`,
      {
        subject: expectation,
        typeofSubject: typeof expectation,
      }
    )
  }

  return false
}
